const COOKIE_NAME = "od_vid";
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
const PROBE_NAME = "od_vid_probe";

export const attributionEndpoint = (origin) => `${origin}/o/t`;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ALLOWED_QUERY_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
  "msclkid",
  "rdt_cid",
  "twclid",
  "tl",
  "odt"
];

const pageState = () => {
  if (!window.__odAttribution) window.__odAttribution = { id: null, written: false };
  return window.__odAttribution;
};

const readCookieValues = (name) => {
  const prefix = name + "=";
  return document.cookie
    .split("; ")
    .filter((part) => part.startsWith(prefix))
    .map((part) => part.slice(prefix.length));
};

const readCookie = (name) => readCookieValues(name)[0] || null;

const readVisitorCookie = () => readCookieValues(COOKIE_NAME).find((value) => UUID_PATTERN.test(value)) || null;

const secureAttribute = () => (window.location.protocol === "https:" ? "; Secure" : "");

const consentWithheld = () => window.odAttributionConsent === false;

const doNotTrackOn = (value) => value === "1" || value === "yes";

const privacySignalOn = () =>
  consentWithheld() ||
  navigator.globalPrivacyControl === true ||
  doNotTrackOn(navigator.doNotTrack) ||
  doNotTrackOn(window.doNotTrack) ||
  navigator.msDoNotTrack === "1";

const uuidV4 = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join("-");
};

const domainCandidates = (hostname) => {
  const labels = (hostname || "").replace(/\.$/, "").split(".");
  const candidates = [];
  for (let i = labels.length - 2; i >= 0; i--) candidates.push(labels.slice(i).join("."));
  return candidates;
};

export const registrableDomain = (hostname) => {
  for (const candidate of domainCandidates(hostname)) {
    document.cookie = `${PROBE_NAME}=1; Domain=${candidate}; Path=/; SameSite=Lax${secureAttribute()}`;
    const stuck = readCookie(PROBE_NAME) !== null;
    document.cookie = `${PROBE_NAME}=; Domain=${candidate}; Path=/; Max-Age=0`;
    if (stuck) return candidate;
  }
  return null;
};

const writeVisitorCookie = (id, domain) => {
  document.cookie =
    `${COOKIE_NAME}=${id}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secureAttribute()}` +
    (domain ? `; Domain=${domain}` : "");
};

const expireVisitorCookie = () => {
  if (readCookie(COOKIE_NAME) === null) return;
  domainCandidates(window.location.hostname).forEach((candidate) => {
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; Domain=${candidate}`;
  });
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/`;
};

const resolveVisitor = () => {
  try {
    const slot = pageState();
    if (privacySignalOn()) {
      slot.id = null;
      slot.written = false;
      expireVisitorCookie();
      return null;
    }

    slot.id = readVisitorCookie() || slot.id || uuidV4();
    if (!slot.written) {
      try {
        writeVisitorCookie(slot.id, registrableDomain(window.location.hostname));
        slot.written = true;
      } catch (e) {}
    }
    return { id: slot.id, persisted: readVisitorCookie() === slot.id };
  } catch (e) {
    return null;
  }
};

export const peekVisitorId = () => {
  try {
    if (privacySignalOn()) return null;
    const slotId = window.__odAttribution && window.__odAttribution.id;
    return readVisitorCookie() || (UUID_PATTERN.test(slotId || "") ? slotId : null);
  } catch (e) {
    return null;
  }
};

export const getVisitorId = () => {
  const visitor = resolveVisitor();
  return visitor ? visitor.id : null;
};

const trimUrl = (href, keepAllowedQuery) => {
  try {
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    let result = url.origin + url.pathname;
    if (keepAllowedQuery) {
      const kept = new URLSearchParams();
      url.searchParams.forEach((value, key) => {
        if (ALLOWED_QUERY_PARAMS.includes(key)) kept.append(key, value);
      });
      const query = kept.toString();
      if (query) result += "?" + query;
    }
    return result;
  } catch (e) {
    return "";
  }
};

const buildPayload = ({ ctx, surface, checkoutToken, linkOnly }) => {
  const visitor = resolveVisitor();
  const payload = {
    v: 1,
    ctx,
    visitor_uuid: visitor ? visitor.id : null,
    surface,
    url: trimUrl(window.location.href, true),
    referrer: trimUrl(document.referrer, false),
    ts: new Date().toISOString()
  };
  if (visitor) payload.vid_persisted = visitor.persisted;
  if (checkoutToken) payload.checkout_token = checkoutToken;
  if (linkOnly) payload.link_only = true;
  return payload;
};

const CONTENT_TYPE = "text/plain;charset=UTF-8";

const send = (endpoint, body) => {
  if (typeof fetch === "function") {
    try {
      const result = fetch(endpoint, {
        method: "POST",
        body,
        keepalive: true,
        credentials: "omit",
        headers: { "Content-Type": CONTENT_TYPE }
      });
      if (result && typeof result.catch === "function") result.catch(() => {});
      return;
    } catch (e) {}
  }
  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(endpoint, new Blob([body], { type: CONTENT_TYPE }));
    }
  } catch (e) {}
};

const MESSAGE_TOKEN_PATTERN = /([?&])odt=[^&]*&?/g;

const removeMessageToken = () => {
  try {
    const search = window.location.search;
    const stripped = search.replace(MESSAGE_TOKEN_PATTERN, "$1").replace(/[?&]$/, "");
    if (stripped === search) return;
    window.history.replaceState(window.history.state, "", window.location.pathname + stripped + window.location.hash);
  } catch (e) {}
};

export const collectTouch = ({ origin, ctx, surface, checkoutToken, linkOnly } = {}) => {
  if (!ctx || !surface) return;
  try {
    const endpoint = attributionEndpoint(origin || window.location.origin);
    send(endpoint, JSON.stringify(buildPayload({ ctx, surface, checkoutToken, linkOnly })));
  } catch (e) {}
  removeMessageToken();
};
